import type { MarginInventoryLatestEntry, MarginMarketInfo } from "@yutai/shared"
import { useEffect, useState } from "react"
import styles from "./MarginCrossCost.module.css"
import type { BenefitOption, BenefitValueOverrides } from "./benefitOptions.js"
import {
  CROSS_KIND_LABELS,
  type CrossKind,
  addDays,
  calcCrossCost,
  daysBetween,
  defaultHoldingDays,
  earliestProfitableDaysBefore,
  maxHoldingDays,
  nextBusinessDay,
  resolveRightsDate,
} from "./crossCost.js"

const yen = (n: number) => `${Math.round(n).toLocaleString("ja-JP")}円`
const signedYen = (n: number) =>
  `${n >= 0 ? "+" : "-"}${Math.abs(Math.round(n)).toLocaleString("ja-JP")}円`
const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"]

function formatDate(iso: string): string {
  const [, m, d] = iso.split("-")
  return `${Number(m)}/${Number(d)}(${WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()]})`
}

/** 「何日前に建てるか」の詳細表に並べる候補（権利付き最終日の◯日前） */
const DAYS_BEFORE_CANDIDATES = [0, 1, 2, 3, 5, 7, 10, 14, 21, 30]

interface Props {
  /** 優待の段階（株数/金額）の選択肢と、いま選択中のもの（切替は行カード側） */
  options: BenefitOption[]
  option: BenefitOption
  market: MarginMarketInfo
  /** 期限ごとの最新在庫（一般信用の売建可否の判定用） */
  inventory: Partial<Record<"14d" | "unlimited", MarginInventoryLatestEntry>>
  /** 今日（YYYY-MM-DD, JST）。CSVの権利付き最終日 MM/DD の年を決める基準 */
  today: string
  /** 証券担保ローンの年率（ページ全体で選択） */
  loanRate: number
  /** 手入力の優待金額（tierの最低株数 → 円）。クロールの値より優先する */
  overrides: BenefitValueOverrides | undefined
  onSaveOverrides: (values: BenefitValueOverrides) => Promise<void>
  /** この銘柄をクロールして優待金額を取り直す（完了はバックグラウンド） */
  onCrawl: () => Promise<void>
}

// 優待金額の手入力欄1行。空欄にして離れると、その tier の上書きを消してクロール値に戻す。
function OverrideRow({
  shares,
  crawledValue,
  override,
  onCommit,
}: {
  shares: number
  crawledValue: number | null
  override: number | undefined
  onCommit: (value: number | null) => Promise<void>
}) {
  const [draft, setDraft] = useState(override === undefined ? "" : String(override))
  const [error, setError] = useState(false)
  useEffect(() => setDraft(override === undefined ? "" : String(override)), [override])

  async function commit() {
    const trimmed = draft.trim().replace(/[,，円]/g, "")
    const value = trimmed === "" ? null : Number(trimmed)
    if (value !== null && !(Number.isInteger(value) && value >= 0)) {
      setError(true)
      return
    }
    setError(false)
    if (value === (override ?? null)) return
    try {
      await onCommit(value)
    } catch (e) {
      console.error(e)
      setError(true)
    }
  }

  return (
    <label className={styles.overrideRow}>
      <span className={styles.overrideLabel}>
        {shares.toLocaleString("ja-JP")}株
        <span className={styles.sub}>
          クロール値: {crawledValue === null ? "なし" : yen(crawledValue)}
        </span>
      </span>
      <input
        className={`${styles.overrideInput} ${error ? styles.overrideError : ""}`}
        type="text"
        inputMode="numeric"
        value={draft}
        placeholder="円（空欄=クロール値）"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
      />
    </label>
  )
}

// 優待クロスの費用/損益の概算（売建側の貸株料等のみ）。
// 「権利付き最終日に建てる（最も利益が高い）」と「今日建てる」を、一般14日/一般無期限で比べる。
export function MarginCrossCost({
  options,
  option,
  market,
  inventory,
  today,
  loanRate,
  overrides,
  onSaveOverrides,
  onCrawl,
}: Props) {
  const [crawlStatus, setCrawlStatus] = useState<"idle" | "starting" | "started" | "error">("idle")

  const rightsIso = market.rightsDate ? resolveRightsDate(market.rightsDate, today) : null
  // 権利付き最終日に建てて翌営業日に返済する保有日数
  const baseDays = rightsIso ? defaultHoldingDays(rightsIso) : 1
  const daysUntilRights = rightsIso ? Math.max(0, daysBetween(today, rightsIso)) : 0

  const kinds: CrossKind[] = ["general14d", "generalUnlimited"]

  /** 権利付き最終日の daysBefore 日前に建てた場合 */
  const scenario = (kind: CrossKind, daysBefore: number) => {
    const days = daysBefore + baseDays
    const feasible = days <= maxHoldingDays(kind)
    const result = calcCrossCost({
      kind,
      shares: option.shares,
      market,
      days,
      benefitValue: option.value,
      loanRate,
    })
    return { days, feasible, result }
  }

  const rows = kinds.map((kind) => {
    const entry = kind === "general14d" ? inventory["14d"] : inventory.unlimited
    // 一般信用は在庫が必要株数に足りないと建てられない
    const noStock = (entry?.quantity ?? 0) < option.shares
    const atRights = scenario(kind, 0)
    const limit = earliestProfitableDaysBefore(kind, atRights.result.breakEvenDays, baseDays)
    return { kind, noStock, atRights, atToday: scenario(kind, daysUntilRights), limit }
  })

  async function crawl() {
    if (crawlStatus === "starting") return
    setCrawlStatus("starting")
    try {
      await onCrawl()
      setCrawlStatus("started")
    } catch (e) {
      console.error(e)
      setCrawlStatus("error")
    }
  }

  async function commitOverride(shares: number, value: number | null) {
    const next: BenefitValueOverrides = { ...(overrides ?? {}) }
    if (value === null) delete next[String(shares)]
    else next[String(shares)] = value
    await onSaveOverrides(next)
  }

  const valueMissing = option.value === null
  const sellAmount = market.price * option.shares
  const returnIso = rightsIso ? nextBusinessDay(rightsIso) : null
  const candidates = rightsIso
    ? [...new Set([...DAYS_BEFORE_CANDIDATES.filter((n) => n < daysUntilRights), daysUntilRights])]
    : []

  const cell = (s: ReturnType<typeof scenario>) =>
    !s.feasible ? (
      <td className={styles.na}>14日超</td>
    ) : (
      <td>
        <span
          className={
            s.result.profit === null ? undefined : s.result.profit >= 0 ? styles.plus : styles.minus
          }
        >
          {s.result.profit === null ? "—" : signedYen(s.result.profit)}
        </span>
        <span className={styles.sub}>
          貸株料{yen(s.result.lendingFee + s.result.extraFee)}+証券ローン{yen(s.result.loanFee)}=
          {yen(s.result.cost)}
        </span>
      </td>
    )

  return (
    <div className={styles.box}>
      <p className={styles.meta}>
        {option.shares.toLocaleString("ja-JP")}株 売建 {yen(sellAmount)}
        {option.value !== null
          ? ` ・ 優待 ${yen(option.value)}`
          : " ・ 優待金額不明（損益は出せません）"}
      </p>
      <div className={styles.valueTools}>
        <button
          type="button"
          className={`${styles.toolButton} ${valueMissing ? styles.toolButtonStrong : ""}`}
          onClick={crawl}
          disabled={crawlStatus === "starting"}
        >
          {crawlStatus === "starting"
            ? "開始中..."
            : valueMissing
              ? "クロールして取得"
              : "再クロール"}
        </button>
        {crawlStatus === "started" && (
          <span className={styles.sub}>クロール開始。完了後に自動で再読込します（20秒ほど）</span>
        )}
        {crawlStatus === "error" && (
          <span className={`${styles.sub} ${styles.minus}`}>
            クロールに失敗しました（公式URL未登録の可能性）
          </span>
        )}
      </div>
      <details className={styles.details}>
        <summary>
          優待金額を手入力
          {options.some((o) => o.overridden) && "（手入力あり・クロール値より優先）"}
        </summary>
        <div className={styles.overrides}>
          {options.map((o) => (
            <OverrideRow
              key={o.shares}
              shares={o.shares}
              crawledValue={o.crawledValue}
              override={overrides?.[String(o.shares)]}
              onCommit={(v) => commitOverride(o.shares, v)}
            />
          ))}
        </div>
      </details>
      <p className={styles.meta}>
        {rightsIso && returnIso
          ? `権利付最終日 ${formatDate(rightsIso)} → 返済 ${formatDate(returnIso)}`
          : "権利付き最終日が不明（当日建て=1日保有で計算）"}
      </p>

      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">区分</th>
            <th scope="col">
              権利付最終日に建てる
              <span className={styles.sub}>{baseDays}日保有</span>
            </th>
            {rightsIso && (
              <th scope="col">
                今日建てる
                <span className={styles.sub}>{daysUntilRights + baseDays}日保有</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ kind, noStock, atRights, atToday, limit }) => (
            <tr key={kind} className={noStock ? styles.dim : undefined}>
              <th scope="row">
                {CROSS_KIND_LABELS[kind]}
                {noStock && <span className={styles.badge}>在庫不足</span>}
                {rightsIso && limit !== null && Number.isFinite(limit) && (
                  <span className={styles.sub}>
                    黒字は{formatDate(addDays(rightsIso, -limit))}建てまで
                  </span>
                )}
                {limit === null && option.value !== null && (
                  <span className={`${styles.sub} ${styles.minus}`}>当日建てでも赤字</span>
                )}
              </th>
              {cell(atRights)}
              {rightsIso && cell(atToday)}
            </tr>
          ))}
        </tbody>
      </table>
      <p className={styles.note}>
        費用は日割りなので、最も利益が高いのは権利付最終日に建てる場合です。早く建てるほど1日ごとに費用が増えます。
      </p>

      {candidates.length > 1 && rightsIso && (
        <details className={styles.details}>
          <summary>何日前に建てるといくら？</summary>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">建てる日</th>
                {kinds.map((k) => (
                  <th key={k} scope="col">
                    {CROSS_KIND_LABELS[k]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {candidates.map((n) => (
                <tr key={n}>
                  <th scope="row">
                    {n === 0 ? "最終日当日" : `${n}日前`}
                    {n === daysUntilRights && n !== 0 && "（今日）"}
                    <span className={styles.sub}>{formatDate(addDays(rightsIso, -n))}</span>
                  </th>
                  {kinds.map((k) => cell(scenario(k, n)))}
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <p className={styles.note}>
        {market.specialFee > 0 && (
          <span className={styles.badge}>特別空売り料 {market.specialFee}円/株/日</span>
        )}
      </p>
    </div>
  )
}
